import React, { useMemo, useState, useEffect, useRef, useId } from 'react';
import './AppointmentCompletionMap.css';

const parseCoord = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const extractCompletionCoords = (appointment = {}) => {
  const tam = appointment.tamamlanmaKonumu || {};
  const lat = parseCoord(tam.latitude ?? tam.enlem);
  const lng = parseCoord(tam.longitude ?? tam.boylam);
  if (lat === null || lng === null) return null;
  return {
    lat,
    lng,
    adres: tam.adres || appointment.tamamlanmaAdresi || '',
    tarih: tam.tamamlanmaTarihi || tam.tarih || appointment.tamamlanmaTarihi,
  };
};

const getGoogleJsKey = () =>
  process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY
  || process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  || '';

const getGoogleEmbedKey = () =>
  process.env.REACT_APP_GOOGLE_MAPS_EMBED_API_KEY
  || process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY
  || process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  || '';

const buildGoogleEmbedUrl = (lat, lng) => {
  const key = getGoogleEmbedKey();
  if (!key) return null;
  const q = encodeURIComponent(`${lat},${lng}`);
  return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${q}&zoom=16&maptype=roadmap`;
};

const buildOsmEmbedUrl = (lat, lng) => {
  const delta = 0.008;
  const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
};

let googleMapsLoaderPromise = null;

const loadGoogleMapsScript = () => {
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }
  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  const apiKey = getGoogleJsKey();
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key missing'));
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.google?.maps) resolve(window.google.maps);
        else reject(new Error('Google Maps failed to load'));
      });
      existingScript.addEventListener('error', reject);
      if (window.google?.maps) resolve(window.google.maps);
      return;
    }

    const callbackName = `__completionMapInit_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      if (window.google?.maps) resolve(window.google.maps);
      else reject(new Error('Google Maps failed to load'));
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      delete window[callbackName];
      reject(new Error('Google Maps script error'));
    };
    document.head.appendChild(script);
  }).catch((err) => {
    googleMapsLoaderPromise = null;
    throw err;
  });

  return googleMapsLoaderPromise;
};

const AppointmentCompletionMap = ({ appointment, height = 340 }) => {
  const completion = useMemo(() => extractCompletionCoords(appointment), [appointment]);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapId = useId().replace(/:/g, '');
  const [renderMode, setRenderMode] = useState('js');
  const [googleFailed, setGoogleFailed] = useState(false);

  useEffect(() => {
    setGoogleFailed(false);
    setRenderMode(getGoogleJsKey() ? 'js' : 'embed');
  }, [completion?.lat, completion?.lng]);

  useEffect(() => {
    if (!completion || renderMode !== 'js' || !mapContainerRef.current) return undefined;

    let cancelled = false;

    const initMap = async () => {
      try {
        const maps = await loadGoogleMapsScript();
        if (cancelled || !mapContainerRef.current) return;

        const map = new maps.Map(mapContainerRef.current, {
          zoom: 16,
          center: { lat: completion.lat, lng: completion.lng },
          mapTypeId: maps.MapTypeId.ROADMAP,
        });

        const marker = new maps.Marker({
          position: { lat: completion.lat, lng: completion.lng },
          map,
          title: 'Tamamlanma konumu',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          },
        });

        const infoContent = `
          <div style="padding: 8px; max-width: 240px;">
            <strong>Tamamlanma Konumu</strong><br/>
            ${completion.adres || 'Adres bilgisi yok'}<br/>
            <small>${completion.lat.toFixed(6)}, ${completion.lng.toFixed(6)}</small>
          </div>
        `;
        const infoWindow = new maps.InfoWindow({ content: infoContent });
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
        infoWindow.open(map, marker);

        mapInstanceRef.current = map;
      } catch {
        if (!cancelled) {
          setRenderMode(getGoogleEmbedKey() ? 'embed' : 'osm');
        }
      }
    };

    initMap();

    return () => {
      cancelled = true;
      mapInstanceRef.current = null;
      if (mapContainerRef.current) {
        mapContainerRef.current.innerHTML = '';
      }
    };
  }, [completion, renderMode]);

  if (!completion) {
    return (
      <div className="appointment-completion-map appointment-completion-map--empty">
        <i className="fas fa-map-marker-alt" aria-hidden="true" />
        <p>Tamamlanma konumu kaydı bulunamadı.</p>
      </div>
    );
  }

  const googleEmbedUrl = buildGoogleEmbedUrl(completion.lat, completion.lng);
  const osmEmbedUrl = buildOsmEmbedUrl(completion.lat, completion.lng);
  const mapHeight = typeof height === 'number' ? `${height}px` : height;
  const useGoogleEmbed = renderMode === 'embed' && Boolean(googleEmbedUrl) && !googleFailed;

  return (
    <div className="appointment-completion-map">
      {renderMode === 'js' ? (
        <div
          ref={mapContainerRef}
          id={`completion-map-${mapId}`}
          className="appointment-completion-map__canvas"
          style={{ height: mapHeight }}
          aria-label="Tamamlanma konumu haritası"
        />
      ) : useGoogleEmbed ? (
        <iframe
          className="appointment-completion-map__iframe"
          src={googleEmbedUrl}
          title="Tamamlanma konumu haritası"
          style={{ height: mapHeight }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          onError={() => setGoogleFailed(true)}
        />
      ) : (
        <iframe
          className="appointment-completion-map__iframe"
          src={osmEmbedUrl}
          title="Tamamlanma konumu haritası"
          style={{ height: mapHeight }}
          allowFullScreen
          loading="lazy"
        />
      )}
      <div className="appointment-completion-map__footer">
        <a
          href={`https://www.google.com/maps?q=${completion.lat},${completion.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="appointment-completion-map__link"
        >
          <i className="fas fa-external-link-alt" aria-hidden="true" />
          Google Maps&apos;te aç
        </a>
      </div>
    </div>
  );
};

export default AppointmentCompletionMap;
export { extractCompletionCoords };
